// Session-expired re-authentication modal (sc-160).
//
// WHY THIS EXISTS
// Expiry was already being detected: Auth.service polls every 20s and, when
// Auth.currentSession() fails, commits TOPBAR:USER_CREDENTIALS_EXPIRED. But
// the only reaction was swapping the header search box for a line of text in
// the top-right corner. The rest of the app stayed fully interactive, so a
// user with a dead session could select and "delete" mail, watch it disappear
// from the UI, and have every API call 401 into a void. Nothing was deleted
// and nothing said so.
//
// This modal blocks interaction until the session is restored, and lets the
// user sign back in WITHOUT leaving the page, so nothing in progress is lost.
//
// NOT DISMISSABLE, by design: a closable "you're logged out" dialog recreates
// the original bug, because the user could dismiss it and keep clicking into
// a dead session. The only exits are a successful login or an explicit
// "sign in as a different user" (which is a real logout).
//
// SECURITY: this is a USABILITY guard, not a security boundary. Removing it
// via devtools yields nothing — the server still rejects unauthorized calls
// with 401. Never treat this, or any client-side check, as protection for
// data. Authorization lives in the API.
import { useState, useRef, useEffect } from 'react'
import { Auth } from 'aws-amplify'
import AuthService from '@/app-domains/user/auth/Auth.service'
import commonsService from '@/services/commons.service'
import useRenderEvent from '@/core/hooks/useRenderEvent'
import { Button } from '@/components/Button/Button'

import './SessionExpiredModal.scoped.sass'

export function SessionExpiredModal () {
  const [visible, setVisible] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const emailRef = useRef()

  useRenderEvent(['TOPBAR:USER_CREDENTIALS_EXPIRED'], () => setVisible(true))

  useEffect(() => {
    if (visible) setTimeout(() => emailRef?.current?.focus(), 50)
  }, [visible])

  // Block background scrolling while the session is dead so the page behind
  // cannot be interacted with by scroll/keyboard.
  useEffect(() => {
    if (!visible) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [visible])

  const signIn = async () => {
    if (!email || !password) {
      setError('Enter your email and password.')
      return
    }
    setSubmitting(true)
    setError('')
    try {
      const res = await Auth.signIn(email, password)
      if (res?.username?.length > 0) {
        // Restore the client session in place: re-read the new token, restart
        // the expiry polling that stopped mattering while we were dead, and
        // dismiss. The user stays exactly where they were — same route, same
        // scroll position, nothing lost.
        AuthService.setUserLogged()
        AuthService.setIntervalForCheckSession()
        setPassword('')
        setVisible(false)
      } else {
        // Amplify returns challenge objects (NEW_PASSWORD_REQUIRED, MFA, ...)
        // rather than throwing. We cannot complete those inline, so hand the
        // user to the full login page which already implements them.
        commonsService.goToPage('/auth')
      }
    } catch (e) {
      // NEVER log the password or the full error object here.
      setError(e?.message || 'Sign in failed. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const onKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); signIn() } }

  // Full logout escape hatch — clears storage and sends the user to /auth.
  const signInAsDifferentUser = () => AuthService.signOut()

  if (!visible) return null

  return (
    <div className='session-expired-overlay' role='dialog' aria-modal='true'
      aria-labelledby='session-expired-title'>
      <div className='session-expired-modal'>
        <h2 id='session-expired-title'>Your session expired</h2>
        <p className='session-expired-lead'>
          You&apos;ve been signed out, so recent actions may not have been saved.
          Sign back in to pick up where you left off.
        </p>

        <label htmlFor='session-expired-email'>Email</label>
        <input id='session-expired-email' ref={emailRef} type='email'
          autoComplete='username' value={email} disabled={submitting}
          onChange={(e) => setEmail(e.target.value)} onKeyDown={onKeyDown} />

        <label htmlFor='session-expired-password'>Password</label>
        <input id='session-expired-password' type='password'
          autoComplete='current-password' value={password} disabled={submitting}
          onChange={(e) => setPassword(e.target.value)} onKeyDown={onKeyDown} />

        {error && <div className='session-expired-error' role='alert'>{error}</div>}

        {/* This codebase's Button takes `content` and `click` — NOT children
            and onClick. Written the React-standard way (as this was until
            sc-177) it renders an empty bar that does nothing, which is exactly
            what shipped. */}
        <Button
          content={submitting ? 'Signing in…' : 'Sign in'}
          click={signIn}
          disabled={submitting}
        />

        <button type='button' className='session-expired-alt' onClick={signInAsDifferentUser}>
          Sign in as a different user
        </button>
      </div>
    </div>
  )
}

export default SessionExpiredModal
